import { create } from 'zustand'
import { buildApiUrl, isScreenshotEntrypoint } from '../config'
import { deserializeBedConfig } from '../utils/stateSerialization'
import { filterToppersForAdjustableBed, filterToppersForWidth, isTopperAvailableForWidth, selectBestTopperModel } from '../utils/topperFiltering'
import { isSaturnOttoman } from '../components/three/ottomanSaturn'
import { isBedOptionAllowed, isBedViewAllowed, restrictBedOptions } from '../utils/bedOptionRestrictions'

// Types for 3D models and textures
interface ThreeDModel {
  id: number
  file_url: string
  preview_url: string | null
  width?: number | null
}

interface BedVariationModel {
  id: number
  width_cm: number
  seamless?: boolean
  three_d_model: ThreeDModel
}

interface ThreeDTexture {
  id: number
  name: string
  base_color_url: string | null
  normal_url: string | null
  roughness_url: string | null
  metalness_url: string | null
  ambient_occlusion_url: string | null
}

interface MaterialTexture {
  id: number
  order?: number
  slot_name: string
  slot_label: string
  texture: ThreeDTexture
  textile_repeat_multiplier?: number
}

interface ProductTextureAssignment {
  id: number
  slot_name: string
  slot_label: string
  texture: ThreeDTexture
  material_id: number | null
  width_cm: number | null
}

interface BedVariationData {
  id: number
  title: string
  slug: string
  models: BedVariationModel[]
  textures: ProductTextureAssignment[]
  models_count: number
  textures_count: number
  bed_type_value?: string | null // e.g., 'adjustable', 'nordic', etc.
  has_accessories?: boolean
}

// Bed configuration types
interface BedComponent {
  id: number
  name: string
  slug: string
  image_url?: string
  price?: number
  // Optional for textile component
  textures?: MaterialTexture[]
  currentTexture?: ThreeDTexture | null
  stitch_color?: string | null
  headboard_textile_for?: {
    id: number
    name: string
    slug: string
    stitch_color?: string | null
    textures: MaterialTexture[]
    currentTexture?: ThreeDTexture | null
  } | null
  // Optional: material type hint for PBR application
  materialType?: 'wood' | 'metal' | 'fabric' | 'leather' | string
  // Optional 3D model and placement for headboard/legs/tables
  three_d_model?: ThreeDModel | null
  three_d_models?: Array<{ width_cm: number | null; is_split: boolean; reclined_side?: string | null; three_d_model: ThreeDModel }> // Full array for toppers (for reclined model lookup)
  on_floor?: boolean
  offsets?: { x?: number; y?: number; z?: number }
  // Legs: scene-unit inset from bed edge (ottoman uses half)
  edge_inset?: number
  height?: number
  legTypeId?: number
}

interface BedConfiguration {
  bed: {
    variation: BedVariationData | null
    selectedSize: number | null
    currentModel: ThreeDModel | null
  }
  textile: BedComponent | null
  selectedTextileId: string | null
  legs: BedComponent | null
  headboard: BedComponent | null
  topper: BedComponent | null
  bedsideTable: BedComponent | null
  ottoman: BedComponent | null
  accessories: BedComponent[]  // Changed from single lamp to array of accessories
}

interface AppState {
  // URL params
  market: string
  lang: string
  bed: string
  
  // Current view state
  currentView: string | null
  
  // Bed configuration
  bedConfig: BedConfiguration
  
  // Loading states
  isLoadingBedVariation: boolean
  isLoadingModels: boolean
  preferSeamless: boolean
  textureResolution: '1k' | '2k' | '4k' | 'original'
  
  // Actions
  setMarket: (market: string) => void
  setLang: (lang: string) => void
  setBed: (bed: string) => void
  setCurrentView: (view: string | null) => void
  setBedComponent: (componentType: keyof Omit<BedConfiguration, 'bed' | 'accessories'>, component: BedComponent | null) => void
  addAccessory: (accessory: BedComponent) => void
  removeAccessory: (accessoryId: number) => void
  toggleAccessory: (accessory: BedComponent) => void
  setSelectedTextile: (id: string | null) => void
  setBedVariation: (data: BedVariationData | null) => void
  setSelectedSize: (size: number | null) => void
  setCurrentModel: (model: ThreeDModel | null) => void
  setLoadingBedVariation: (loading: boolean) => void
  setLoadingModels: (loading: boolean) => void
  setPreferSeamless: (prefer: boolean) => void
  setTextureResolution: (res: '1k' | '2k' | '4k') => void
  // Map a selected headboard option (from API) into store
  setHeadboardFromApi: (option: any | null) => void
  
  // Initialize from URL params
  initializeFromURL: () => void
  // Hydrate state from backend bed-config endpoint for screenshots
  hydrateFromBackendConfig: (encodedConfig: string) => Promise<void>
  
  // Restore state from encoded config
  restoreFromConfig: (config: any) => Promise<void>
  
  // Load bed variation data
  loadBedVariation: (slug: string) => Promise<void>
  
  // Select size and load corresponding model
  selectSize: (size: number, skipTopperAutoSelect?: boolean) => Promise<void>
  refreshCurrentModelForSelectedSize: () => void
  
  // Get computed values
  getCurrentBedSize: () => number | null
  getCurrentModel: () => ThreeDModel | null
  getBedVariation: () => BedVariationData | null
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  market: '',
  lang: '',
  bed: '',
  currentView: null,
  
  // Bed configuration
  bedConfig: {
    bed: {
      variation: null,
      selectedSize: null,
      currentModel: null
    },
    textile: null,
    selectedTextileId: null,
    legs: null,
    headboard: null,
    topper: null,
    bedsideTable: null,
    ottoman: null,
    accessories: []
  },
  
  // Loading states
  isLoadingBedVariation: false,
  isLoadingModels: false,
  preferSeamless: false,
  textureResolution: '1k',
  
  // Actions
  setMarket: (market: string) => set({ market }),
  setLang: (lang: string) => set({ lang }),
  setBed: (bed: string) => set({ bed }),
  setCurrentView: (view: string | null) => set({
    currentView: isBedViewAllowed(get().bedConfig.bed.variation, view) ? view : null,
  }),
  setBedComponent: (componentType, component) => set((state) => ({
    bedConfig: restrictBedOptions(state.bedConfig.bed.variation, {
      ...state.bedConfig,
      [componentType]: component
    })
  })),

  setSelectedTextile: (id) => set((state) => ({
    bedConfig: {
      ...state.bedConfig,
      selectedTextileId: id
    }
  })),
  setBedVariation: (data: BedVariationData | null) => set((state) => ({
    currentView: isBedViewAllowed(data, state.currentView) ? state.currentView : null,
    bedConfig: restrictBedOptions(data, {
      ...state.bedConfig,
      bed: {
        ...state.bedConfig.bed,
        variation: data
      }
    })
  })),
  setSelectedSize: (size: number | null) => set((state) => ({
    bedConfig: {
      ...state.bedConfig,
      bed: {
        ...state.bedConfig.bed,
        selectedSize: size
      }
    }
  })),
  setCurrentModel: (model: ThreeDModel | null) => set((state) => ({
    bedConfig: {
      ...state.bedConfig,
      bed: {
        ...state.bedConfig.bed,
        currentModel: model
      }
    }
  })),
  setLoadingBedVariation: (loading: boolean) => set({ isLoadingBedVariation: loading }),
  setLoadingModels: (loading: boolean) => set({ isLoadingModels: loading }),
  setPreferSeamless: (prefer: boolean) => set({ preferSeamless: prefer }),
  setTextureResolution: (res) => set({ textureResolution: res }),

  // Explicitly set headboard from API option object
  setHeadboardFromApi: (option: any | null) => set((state) => ({
    bedConfig: {
      ...state.bedConfig,
      headboard: isBedOptionAllowed(state.bedConfig.bed.variation, 'headboard') && option && option.three_d_model ? {
        id: option.id,
        name: option.title,
        slug: option.slug,
        three_d_model: option.three_d_model,
        on_floor: !!option.on_floor,
        offsets: option.offsets || { x: 0, y: 0, z: 0 },
        texture_options: option.texture_options || [],
        currentTexture: ((option.texture_options || [])[0]?.textures || [])[0] || null,
      } : null
    }
  })),
  
  // Initialize from URL params
  initializeFromURL: () => {
    const urlParams = new URLSearchParams(window.location.search)
    
    // Get basic params from URL (prefer URL params over config for readability)
    const marketFromUrl = urlParams.get('market')
    const langFromUrl = urlParams.get('lang')
    const bedFromUrl = urlParams.get('bed')
    
    // Check for encoded config for full state restoration
    const encodedConfig = urlParams.get('config')
    let decodedConfig = null
    if (encodedConfig) {
      decodedConfig = deserializeBedConfig(encodedConfig)
    }
    
    // Use URL params if available, otherwise fall back to config or defaults
    const market = marketFromUrl || decodedConfig?.market || 'se'
    const lang = langFromUrl || decodedConfig?.lang || 'sv'
    // Clean bed value - remove any query parameters that might have been included
    const bedSlug = bedFromUrl || (decodedConfig?.config?.bed as any)?.slug || ''
    const bed = bedSlug.split('?')[0].split('/')[0]
    
    set({ market, lang, bed })
    
    // Store config for restoration if it exists
    if (decodedConfig) {
      ;(window as any).__pendingConfig__ = decodedConfig.config
      ;(window as any).__pendingConfigEncoded__ = encodedConfig
    }
    
    // Decide texture resolution
    const tex = (urlParams.get('tex') as '1k'|'2k'|'4k' | 'original' | null)
    let res: '1k'|'2k'|'4k' | 'original' = '1k'

    // For the dedicated /screenshot entrypoint, always use highest available
    // resolution to maximize offline render quality, regardless of network.
    if (isScreenshotEntrypoint()) {
      res = '4k'
    } else if (tex === '1k' || tex === '2k' || tex === '4k' || tex === 'original') {
      res = tex
    } else {
      const conn: any = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
      const et = conn?.effectiveType as string | undefined
      if (et === 'slow-2g' || et === '2g') res = '1k'
      else if (et === '3g') res = '2k'
      else res = '2k' // default for 4g/unknown
    }
    set({ textureResolution: res })
    
    // Update URL if essential params are missing
    if (!marketFromUrl || !langFromUrl || !bedFromUrl) {
      const newParams = new URLSearchParams()
      newParams.set('market', market)
      newParams.set('lang', lang)
      if (bed) newParams.set('bed', bed)
      // Preserve config if it exists
      if (encodedConfig) {
        newParams.set('config', encodedConfig)
      }
      // Do not surface tex param by default (non user-facing)
      
      const newURL = `${window.location.pathname}?${newParams.toString()}`
      window.history.replaceState({}, '', newURL)
    }
  },
  
  // Restore state from serialized config (called after bed variation is loaded)
  restoreFromConfig: async (config: any) => {
    const { market, lang } = get()
    const bedVariation = get().getBedVariation()
    if (!bedVariation) {
      console.warn('[restoreFromConfig] Cannot restore config: bed variation not loaded', {
        market,
        lang,
        bedVariation: bedVariation,
      })
      return
    }

    config = restrictBedOptions(bedVariation, config)
    
    // Set flag to prevent selectSize from overwriting restored components
    ;(window as any).__isRestoringFromConfig__ = true
    
    try {
      // Restore preferSeamless setting first (before selecting size, as it affects model selection)
      if (config.preferSeamless !== undefined) {
        get().setPreferSeamless(config.preferSeamless)
      }
      
      // Set bed size first - this also loads the model
      // Skip topper auto-selection since we'll restore it from config
      const selectedSize = config.bed.selectedSize
      if (selectedSize) {
        await get().selectSize(selectedSize, true) // Pass true to skip topper auto-selection
        // Wait a bit to ensure the model is set before restoring other components
        // This is important because Scene component checks for currentModel before rendering headboard
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      
      // Get bed width for legs API call - use config value since selectSize is synchronous
      const bedWidth = selectedSize || 180
      
      // Fetch only the option endpoints we actually need for this config.
      // This reduces unnecessary network load, especially in screenshot
      // mode where some components may not be present in the config.
      const materialsPromise = config.textile?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/materials/?market=${market}&lang=${lang}`))
        : Promise.resolve(null as any)
      const legsPromise = config.legs?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/legs/?market=${market}&lang=${lang}&width_cm=${bedWidth}`))
        : Promise.resolve(null as any)
      const toppersPromise = config.topper?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/toppers/?market=${market}&lang=${lang}`))
        : Promise.resolve(null as any)
      const headboardsPromise = config.headboard?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/headboards/?market=${market}&lang=${lang}&width_cm=${bedWidth}`))
        : Promise.resolve(null as any)
      const bedsideTablesPromise = config.bedsideTable?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/bedsidetables/?market=${market}&lang=${lang}`))
        : Promise.resolve(null as any)
      const ottomansPromise = config.ottoman?.id
        ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/ottomans/?market=${market}&lang=${lang}`))
        : Promise.resolve(null as any)
      const accessoriesPromise =
        Array.isArray(config.accessories) && config.accessories.length > 0
          ? fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/lamps/?market=${market}&lang=${lang}`))
          : Promise.resolve(null as any)

      // Fetch all required option endpoints in parallel, but handle errors gracefully
      const fetchResults = await Promise.allSettled([
        materialsPromise,
        legsPromise,
        toppersPromise,
        headboardsPromise,
        bedsideTablesPromise,
        ottomansPromise,
        accessoriesPromise,
      ])
      
      // Extract responses, handling both fulfilled and rejected promises
      const materialsRes = fetchResults[0].status === 'fulfilled' ? fetchResults[0].value : null
      const legsRes = fetchResults[1].status === 'fulfilled' ? fetchResults[1].value : null
      const toppersRes = fetchResults[2].status === 'fulfilled' ? fetchResults[2].value : null
      const headboardsRes = fetchResults[3].status === 'fulfilled' ? fetchResults[3].value : null
      const bedsideTablesRes = fetchResults[4].status === 'fulfilled' ? fetchResults[4].value : null
      const ottomansRes = fetchResults[5].status === 'fulfilled' ? fetchResults[5].value : null
      const accessoriesRes = fetchResults[6].status === 'fulfilled' ? fetchResults[6].value : null
      
      // Restore textile
      if (config.textile?.id && materialsRes?.ok) {
        try {
          const mats = await materialsRes.json()
          const material = (mats?.options || []).find((m: any) => m.id === config.textile.id || m.id.toString() === config.textile.id.toString())
          if (material) {
            let selectedTexture = material.textures?.[0]?.texture || null
            if (config.textileTextureId) {
              const texture = material.textures?.find((t: any) => t.texture?.id === config.textileTextureId)?.texture
              if (texture) selectedTexture = texture
            }
            const headboardTextile = material.headboard_textile_for || null
            const selectedHeadboardTexture = headboardTextile?.textures?.[0]?.texture || null
            get().setSelectedTextile(material.id)
            get().setBedComponent('textile', {
              id: material.id,
              name: material.title,
              slug: material.slug,
              textures: material.textures,
              currentTexture: selectedTexture,
              stitch_color: material.stitch_color,
              headboard_textile_for: headboardTextile ? {
                id: headboardTextile.id,
                name: headboardTextile.title,
                slug: headboardTextile.slug,
                stitch_color: headboardTextile.stitch_color,
                textures: headboardTextile.textures || [],
                currentTexture: selectedHeadboardTexture,
              } : null,
            } as any)
          }
        } catch (err) {
          console.warn('Failed to restore textile:', err)
        }
      }
      
      // Restore legs
      if (config.legs?.id && legsRes?.ok) {
        try {
          const legsData = await legsRes.json()
        const legTypes = legsData.leg_types || {}
        
        // Find the leg across all types, prefer the legTypeId if available
        let foundLeg = null
        let foundLegType = null
        
        if (config.legTypeId) {
          // If we have legTypeId, find the leg type first, then the leg
          for (const [typeTitle, legType] of Object.entries(legTypes) as [string, any][]) {
            if (legType.id === config.legTypeId) {
              foundLegType = legType
              foundLeg = legType.legs?.find((l: any) => l.id === config.legs.id || l.id.toString() === config.legs.id.toString())
              if (foundLeg) break
            }
          }
        }
        
        // If not found by legTypeId, search all types
        if (!foundLeg) {
          for (const [typeTitle, legType] of Object.entries(legTypes) as [string, any][]) {
            foundLeg = legType.legs?.find((l: any) => l.id === config.legs.id || l.id.toString() === config.legs.id.toString())
            if (foundLeg) {
              foundLegType = legType
              break
            }
          }
        }
        
        if (foundLeg && foundLegType) {
          const slots = foundLeg.texture_options || []
          let selectedTexture = slots[0]?.textures?.[0] || null
          if (config.legTextureId) {
            const texture = slots.find((s: any) => s.textures?.some((t: any) => t.id === config.legTextureId))?.textures?.find((t: any) => t.id === config.legTextureId)
            if (texture) selectedTexture = texture
          }
          get().setBedComponent('legs', {
            id: foundLeg.id,
            name: foundLeg.title,
            slug: foundLeg.slug,
            height: foundLeg.height,
            edge_inset: foundLeg.edge_inset,
            legTypeId: foundLegType.id, // Use the leg type ID from the structure
            three_d_model: foundLeg.three_d_model,
            texture_options: slots,
            currentTexture: selectedTexture,
          } as any)
        } else {
          console.warn('Leg not found for restoration:', config.legs.id, 'legTypeId:', config.legTypeId)
        }
        } catch (err) {
          console.warn('Failed to restore legs:', err)
        }
      }
      
      // Restore topper
      if (config.topper?.id && toppersRes?.ok) {
        try {
          const toppersData = await toppersRes.json()
          const topper = (toppersData?.options || []).find((t: any) => t.id === config.topper.id || t.id.toString() === config.topper.id.toString())
          if (topper && isTopperAvailableForWidth(topper, config.bed.selectedSize || 0)) {
            const models = topper.three_d_models || []
            const selectedWidth = config.bed.selectedSize
            
            // Check if bed is adjustable to use proper model selection logic
            const currentBedVariation = get().bedConfig.bed.variation
            const isAdjustable = currentBedVariation?.bed_type_value === 'adjustable'
            
            // Use selectBestTopperModel for proper model selection (handles adjustable beds correctly)
            // This ensures adjustable beds get reclined models or split toppers as appropriate
            const chosenModel = selectBestTopperModel(
              models,
              selectedWidth || 0,
              isAdjustable,
              isAdjustable ? null : (topper.three_d_model || null)
            )
            
            let selectedTexture = null
            if (topper.texture_options && topper.texture_options.length > 0) {
              selectedTexture = topper.texture_options[0].textures?.[0] || null
              if (config.topperTextureId) {
                const texture = topper.texture_options.find((opt: any) => 
                  opt.textures?.some((t: any) => t.id === config.topperTextureId)
                )?.textures?.find((t: any) => t.id === config.topperTextureId)
                if (texture) selectedTexture = texture
              }
            }
            
            get().setBedComponent('topper', {
              id: topper.id,
              name: topper.title,
              slug: topper.slug,
              offsets: topper.offsets || { x: 0, y: 0, z: 0 },
              three_d_model: chosenModel,
              three_d_models: models, // Store full array for reclined model lookup (needed for Scene.tsx)
              texture_options: topper.texture_options || [],
              currentTexture: selectedTexture,
            } as any)
          } else {
            get().setBedComponent('topper', null)
          }
        } catch (err) {
          console.warn('Failed to restore topper:', err)
        }
      }
      
      // Restore bedside table
      
      if (config.bedsideTable?.id) {
        if (!bedsideTablesRes?.ok) {
          console.warn('[restoreFromConfig] Bedside tables API response not ok:', {
            status: bedsideTablesRes?.status,
            statusText: bedsideTablesRes?.statusText,
            ok: bedsideTablesRes?.ok
          })
        } else {
          try {
            const tablesData = await bedsideTablesRes.json()
            
            const table = (tablesData?.options || []).find((t: any) => t.id === config.bedsideTable.id || t.id.toString() === config.bedsideTable.id.toString())
            if (!table) {
              console.warn('[restoreFromConfig] Bedside table not found for restoration:', {
                requestedId: config.bedsideTable.id,
                requestedIdType: typeof config.bedsideTable.id,
                availableIds: (tablesData?.options || []).map((t: any) => ({ id: t.id, idType: typeof t.id }))
              })
            } else if (!table.three_d_model) {
              console.warn('[restoreFromConfig] Bedside table found but missing three_d_model:', {
                tableId: table.id,
                tableTitle: table.title,
                hasThreeDModel: !!table.three_d_model
              })
            } else {
              const slots = table.texture_options || []
              let selectedTexture = slots[0]?.textures?.[0] || null
              if (config.bedsideTableTextureId) {
                const texture = slots.find((s: any) => s.textures?.some((t: any) => t.id === config.bedsideTableTextureId))?.textures?.find((t: any) => t.id === config.bedsideTableTextureId)
                if (texture) selectedTexture = texture
              }
              get().setBedComponent('bedsideTable', {
                id: table.id,
                name: table.title,
                slug: table.slug,
                on_floor: table.on_floor,
                offsets: table.offsets || { x: 0, y: 0, z: 0 },
                three_d_model: table.three_d_model,
                texture_options: slots,
                currentTexture: selectedTexture,
              } as any)
            }
          } catch (err) {
            console.error('[restoreFromConfig] Failed to restore bedside table:', err)
          }
        }
      }
      
      // Restore ottoman
      if (config.ottoman?.id && ottomansRes?.ok) {
        try {
          const ottomansData = await ottomansRes.json()
          const ottoman = (ottomansData?.options || []).find((o: any) => o.id === config.ottoman.id || o.id.toString() === config.ottoman.id.toString())
          if (ottoman && ottoman.three_d_model) {
            const isSaturn = [ottoman.title, ottoman.slug, ottoman.slug_en, ottoman.slugEn]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes('saturn')
            const slots = ottoman.texture_options || []
            // Saturn uses bed textile (will be handled in Scene), others use their own texture
            let selectedTexture = null
            if (!isSaturn && slots.length > 0) {
              selectedTexture = slots[0]?.textures?.[0] || null
              if (config.ottomanTextureId) {
                const texture = slots.find((s: any) => s.textures?.some((t: any) => t.id === config.ottomanTextureId))?.textures?.find((t: any) => t.id === config.ottomanTextureId)
                if (texture) selectedTexture = texture
              }
            }
            get().setBedComponent('ottoman', {
              id: ottoman.id,
              name: ottoman.title,
              slug: ottoman.slug,
              slug_en: ottoman.slug_en || ottoman.slugEn,
              three_d_model: ottoman.three_d_model,
              texture_options: slots,
              currentTexture: selectedTexture,
            } as any)
          }
        } catch (err) {
          console.warn('Failed to restore ottoman:', err)
        }
      }
      
      // Restore accessories
      if (config.accessories && config.accessories.length > 0 && accessoriesRes?.ok) {
        try {
          const accessoriesData = await accessoriesRes.json()
          const selectedAccessories = (accessoriesData?.options || []).filter((acc: any) =>
            config.accessories.some((cAcc: any) => acc.id === cAcc.id || acc.id.toString() === cAcc.id.toString())
          )
          selectedAccessories.forEach((acc: any) => {
            get().toggleAccessory({
              id: acc.id,
              name: acc.title,
              slug: acc.slug,
              image_url: acc.product_thumbnail,
              three_d_model: acc.three_d_model,
              texture_options: acc.texture_options,
              currentTexture: acc.texture_options?.[0]?.textures?.[0] || null,
            } as any)
          })
        } catch (err) {
          console.warn('Failed to restore accessories:', err)
        }
      }
      
      // Restore headboard LAST - after everything else is set, especially after model is ready
      // This ensures currentModel is set before headboard, which Scene component requires
      if (config.headboard?.id && headboardsRes?.ok) {
        try {
          const headboardsData = await headboardsRes.json()
          const headboard = (headboardsData?.options || []).find((h: any) => h.id === config.headboard.id || h.id.toString() === config.headboard.id.toString())
          if (headboard) {
            // Ensure currentModel is set before restoring headboard
            let currentModel = get().bedConfig.bed.currentModel
            if (!currentModel) {
              console.warn('Waiting for currentModel before restoring headboard...')
              // Wait a bit more for model to be set
              await new Promise(resolve => setTimeout(resolve, 200))
              currentModel = get().bedConfig.bed.currentModel
            }
            
            // Headboards can have width-specific models like toppers
            // Use the actual selected size from state (should be set by selectSize above)
            const currentSize = get().bedConfig.bed.selectedSize || selectedSize
            const models = (headboard.three_d_models || []) as Array<{ 
              width_cm: number | null
              three_d_model: { id: number, file_url: string, preview_url?: string | null } 
            }>

            // Only accept a model that exactly matches the current width
            const chosenModel = currentSize
              ? models.find(m => m.width_cm === currentSize)?.three_d_model || null
              : null

            if (chosenModel && chosenModel.file_url) {
              // Use setHeadboardFromApi but with modified headboard object that has the chosen model
              const headboardWithChosenModel = {
                ...headboard,
                three_d_model: chosenModel
              }
              get().setHeadboardFromApi(headboardWithChosenModel)
              // Force a state update to ensure React re-renders
              // Trigger a dummy update that React can't batch with the headboard update
              get().setCurrentView(get().currentView)
              // Wait a bit for React to process the state update
              await new Promise(resolve => setTimeout(resolve, 100))
            } else {
              console.warn('Headboard found but missing three_d_model or file_url:', {
                headboard,
                hasThreeDModel: !!headboard.three_d_model,
                hasModels: models.length > 0,
                currentSize,
                chosenModel
              })
            }
          } else {
            console.warn('Headboard not found for restoration:', config.headboard.id, 'Available IDs:', (headboardsData?.options || []).map((h: any) => h.id))
          }
        } catch (err) {
          console.warn('Failed to restore headboard:', err)
        }
      } else if (config.headboard?.id) {
        console.warn('Headboard restoration skipped - headboardsRes:', headboardsRes ? (headboardsRes.ok ? 'ok' : 'not ok') : 'null', 'config.headboard.id:', config.headboard.id)
      }
    } catch (error) {
      console.error('Error restoring config:', error)
    } finally {
      // Reset flag
      ;(window as any).__isRestoringFromConfig__ = false
      
      // Wait a bit for textures and models to start loading, then dispatch event
      // This allows external tools (like Playwright screenshot service) to wait for full load
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('configRestored', {
          detail: {
            timestamp: Date.now(),
            config: config
          }
        }))
      }, 1000) // Wait 1s after config restoration for textures/models to start loading
    }
  },

  // Hydrate state from backend bed-config endpoint for screenshots
  hydrateFromBackendConfig: async (encodedConfig: string) => {
    const response = await fetch(buildApiUrl(`bed-config/?config=${encodeURIComponent(encodedConfig)}`))
    if (!response.ok) {
      const text = await response.text()
      console.error('Failed to load bed-config from backend:', response.status, text)
      throw new Error(`bed-config failed: ${response.status}`)
    }
    const data = await response.json()
    if (!data?.bed?.selectedSize) {
      console.error('bed-config response missing bed.selectedSize', data)
      throw new Error('bed-config missing selectedSize')
    }

    if (data.topper) {
      const isAdjustable = data.bed?.variation?.bed_type_value === 'adjustable'
      const models = data.topper.three_d_models || []
      const chosen = selectBestTopperModel(
        models,
        data.bed.selectedSize,
        isAdjustable,
        isAdjustable ? null : (data.topper.three_d_model || null),
      )
      if (chosen) {
        data.topper.three_d_model = chosen
      }
    }
    // Screenshot hydration: keep render-ready false until Scene reports textiles/topper ready.
    ;(window as any).__bed_render_ready__ = false

    // URL config carries slugEn (e.g. saturn-2017); bed-config may only have localized title/slug.
    let configOttoman: any = null
    let ottomanTextureId: number | null = null
    try {
      const decoded = deserializeBedConfig(encodedConfig)
      configOttoman = decoded?.config?.ottoman || null
      ottomanTextureId = (decoded?.config as any)?.ottomanTextureId ?? null
    } catch {
      /* ignore decode errors; API payload alone is enough when title/slug match */
    }

    let ottoman = data.ottoman
      ? {
          ...data.ottoman,
          name: data.ottoman.name || data.ottoman.title,
          slug_en:
            data.ottoman.slug_en ||
            data.ottoman.slugEn ||
            configOttoman?.slugEn ||
            configOttoman?.slug_en,
        }
      : null

    if (ottoman && !isSaturnOttoman(ottoman) && !ottoman.currentTexture) {
      const slots = ottoman.texture_options || []
      let selected = null as any
      if (ottomanTextureId) {
        for (const slot of slots) {
          const hit = (slot.textures || []).find((t: any) => t.id === ottomanTextureId)
          if (hit) {
            selected = hit
            break
          }
        }
      }
      ottoman = {
        ...ottoman,
        currentTexture: selected || slots[0]?.textures?.[0] || null,
      }
    }

    set((state) => {
      const apiVariation = data.bed?.variation || state.bedConfig.bed.variation
      // Keep language-correct slug from URL/`bed` store when bed-config returns slug_en.
      const urlBedSlug = get().bed
      const variation =
        apiVariation && urlBedSlug && apiVariation.slug !== urlBedSlug
          ? { ...apiVariation, slug: urlBedSlug, slug_en: apiVariation.slug_en || apiVariation.slug }
          : apiVariation

      return {
        currentView: isBedViewAllowed(variation, state.currentView) ? state.currentView : null,
        bedConfig: restrictBedOptions(variation, {
          ...state.bedConfig,
          bed: {
            ...state.bedConfig.bed,
            variation,
            selectedSize: data.bed?.selectedSize || state.bedConfig.bed.selectedSize,
            currentModel: data.bed?.currentModel || state.bedConfig.bed.currentModel,
          },
          textile: data.textile || null,
          legs: data.legs || null,
          headboard: data.headboard || null,
          topper: data.topper || null,
          bedsideTable: data.bedsideTable || null,
          ottoman,
          accessories: data.accessories || [],
        }),
      }
    })
  },
  
  // Load bed variation data
  loadBedVariation: async (slug: string) => {
    const { market, lang } = get()
    set({ isLoadingBedVariation: true })
    
    try {
      // Use the same pattern as the working API call - include market and lang params
      const response = await fetch(buildApiUrl(`bed-variations/${slug}/?market=${market}&lang=${lang}`))
      if (response.ok) {
        const data = await response.json()

        
        if (data.id && data.title && data.slug) {
          // Create the bed variation object with the API data
          const bedVariationData = {
            id: data.id,
            title: data.title,
            slug: data.slug,
            models: data.models || [],
            textures: data.textures || [],
            models_count: (data.models || []).length,
            textures_count: (data.textures || []).length,
            bed_type_value: data.bed_type_value || null,
            has_accessories: typeof data.has_accessories === 'boolean' ? data.has_accessories : false,
          }
          
          // Setting bed variation data
          get().setBedVariation(bedVariationData)

          
          // Check if there's a pending config to restore (from URL)
          const pendingConfig = (window as any).__pendingConfig__
          const pendingConfigEncoded = (window as any).__pendingConfigEncoded__
          if (pendingConfig) {
            delete (window as any).__pendingConfig__
            const screenshotEntrypoint = isScreenshotEntrypoint()

            if (screenshotEntrypoint && pendingConfigEncoded) {
              try {
                await get().hydrateFromBackendConfig(pendingConfigEncoded)
              } catch (err) {
                console.error('[loadBedVariation] hydrateFromBackendConfig failed, falling back to restoreFromConfig', err)
                await get().restoreFromConfig(pendingConfig)
              }
            } else {
              await get().restoreFromConfig(pendingConfig)
            }
          } else {
            // Don't auto-select any size - let user choose
            // Available sizes
            
              // Auto-select first available textile, legs
              // Fetch all endpoints in parallel; failure of one should not block the other
              // Include width_cm if size is already selected
              const selectedSize = get().bedConfig.bed.selectedSize
              const legsUrl = selectedSize 
                ? `bed-variations/${data.slug}/legs/?market=${market}&lang=${lang}&width_cm=${selectedSize}`
                : `bed-variations/${data.slug}/legs/?market=${market}&lang=${lang}`
              try {
                const [materialsRes, legsRes] = await Promise.all([
                  fetch(buildApiUrl(`bed-variations/${data.slug}/materials/?market=${market}&lang=${lang}`)),
                  isBedOptionAllowed(bedVariationData, 'legs')
                    ? fetch(buildApiUrl(legsUrl))
                    : Promise.resolve(null),
                ])
              
              // Handle textiles (materials)
              if (materialsRes.ok) {
                const mats = await materialsRes.json()
                const firstMaterial = (mats?.options || [])[0]
                if (firstMaterial && firstMaterial.textures && firstMaterial.textures.length > 0) {
                  const firstTexture = firstMaterial.textures[0]?.texture || null
                  const headboardTextile = firstMaterial.headboard_textile_for || null
                  const selectedHeadboardTexture = headboardTextile?.textures?.[0]?.texture || null
                  // Store selection
                  get().setSelectedTextile(firstMaterial.id)
                  get().setBedComponent('textile', {
                    id: firstMaterial.id,
                    name: firstMaterial.title,
                    slug: firstMaterial.slug,
                    textures: firstMaterial.textures,
                    currentTexture: firstTexture,
                    stitch_color: firstMaterial.stitch_color,
                    headboard_textile_for: headboardTextile ? {
                      id: headboardTextile.id,
                      name: headboardTextile.title,
                      slug: headboardTextile.slug,
                      stitch_color: headboardTextile.stitch_color,
                      textures: headboardTextile.textures || [],
                      currentTexture: selectedHeadboardTexture,
                    } : null,
                  } as any)
                  // removed logs
                }
              }
              
              // Handle legs
              if (legsRes?.ok) {
                const legsData = await legsRes.json()
                // New API structure: legsData.leg_types contains the grouped legs
                const legTypes = legsData.leg_types || {}
                // Prefer '4 Legs' as initial type if available, else fallback to first
                const legTypeTitles = Object.keys(legTypes)
                const preferredTitle = legTypeTitles.find(t => t.toLowerCase().includes('4')) || legTypeTitles[0]
                const preferredLegType = preferredTitle ? (legTypes as any)[preferredTitle] : null
                const firstLeg = preferredLegType?.legs?.[0]
                
                if (firstLeg && firstLeg.three_d_model) {
                  const slots = (firstLeg.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: any[] }>
                  const firstSlot = slots[0]
                  const textures = firstSlot?.textures || []
                  
                  // Get current bed width to find matching texture
                  const selectedSize = get().bedConfig.bed.selectedSize
                  const currentModelWidth = get().bedConfig.bed.currentModel?.width
                  const bedWidth = selectedSize || currentModelWidth || null
                  
                  // Find texture that matches current bed width (exact match preferred)
                  let selectedTexture = textures[0] || null
                  if (bedWidth !== null && textures.length > 0) {
                    // First try exact width match
                    const exactMatch = textures.find((t: any) => {
                      const tWidth = t.width_cm
                      const bedWidthNum = Number(bedWidth)
                      return tWidth === bedWidthNum || tWidth === bedWidth || Number(tWidth) === bedWidthNum
                    })
                    if (exactMatch) {
                      selectedTexture = exactMatch
                    }
                  }
                  
                  const firstLegTypeTitle = preferredTitle
                  const firstLegType = preferredLegType
                  get().setBedComponent('legs', {
                    id: firstLeg.id,
                    name: firstLeg.title,
                    slug: firstLeg.slug,
                    height: (firstLeg as any).height,
                    edge_inset: (firstLeg as any).edge_inset,
                    legTypeId: firstLegType?.id, // Only store the leg type ID
                    three_d_model: firstLeg.three_d_model,
                    texture_options: slots,
                    currentTexture: selectedTexture,
                  } as any)
                  // removed logs
                }
              }
              
              // Do NOT auto-select toppers - wait for bed size to be selected first
              // Toppers will be selected when selectSize() is called

              // Do not auto-select headboards; selection is user-driven elsewhere
            } catch (autoErr) {
              console.warn('Auto-select of textile/legs/toppers failed:', autoErr)
            }
          }

          // Convert media URLs to use the backend base URL
          // keep full backend URLs for media files
        } else {
          console.error('❌ Invalid API response structure')
          get().setBedVariation(null)
        }
      } else {
        console.error('Failed to load bed variation:', response.statusText)
        get().setBedVariation(null)
      }
    } catch (error) {
      console.error('Error loading bed variation:', error)
      get().setBedVariation(null)
    } finally {
      set({ isLoadingBedVariation: false })
    }
  },
  
  // Select size and load corresponding model
  selectSize: async (size: number, skipTopperAutoSelect: boolean = false) => {
    const bedVariation = get().getBedVariation()
    if (!bedVariation) return
    
    // Honor preferSeamless flag, otherwise prefer non-seamless
    const preferSeamless = get().preferSeamless
    const candidates = bedVariation.models.filter((m: BedVariationModel) => m.width_cm === size)

    const modelAssignment = preferSeamless
      ? (candidates.find((m) => m.seamless === true) || candidates.find((m) => m.seamless !== false) || candidates[0])
      : (candidates.find((m) => m.seamless === false) || candidates[0])
    // Only update state if size actually changes
    const prevSize = get().bedConfig.bed.selectedSize
    if (modelAssignment) {
      if (prevSize !== size) {
        get().setSelectedSize(size)
      }
      get().setCurrentModel(modelAssignment.three_d_model)
    } else {
      console.warn(`No 3D model found for size ${size}cm`)
      if (prevSize !== size) {
        get().setSelectedSize(size)
      }
      get().setCurrentModel(null)
    }
    
    // Now select topper matching the bed size
    // Skip auto-selection if flag is set (e.g., when restoring from config) or topper already exists
    const isRestoringFromConfig = (window as any).__isRestoringFromConfig__
    if (skipTopperAutoSelect || isRestoringFromConfig) {
      return // Skip topper auto-selection (will be restored from config)
    }
    
    const existingTopper = get().bedConfig.topper
    if (existingTopper) {
      if (!isTopperAvailableForWidth(existingTopper, size)) {
        get().setBedComponent('topper', null)
        return
      }
      // Topper already exists, but update its model if needed for the new size
      // Only update if we have three_d_models array (restored toppers have this)
      const models = existingTopper.three_d_models || []
      if (models.length > 0) {
        const isAdjustable = bedVariation.bed_type_value === 'adjustable'
        const updatedModel = selectBestTopperModel(
          models,
          size,
          isAdjustable,
          existingTopper.three_d_model || null
        )
        
        // Only update if the model changed
        if (updatedModel && updatedModel.file_url !== existingTopper.three_d_model?.file_url) {
          get().setBedComponent('topper', {
            ...existingTopper,
            three_d_model: updatedModel,
          } as any)
        }
      }
      return // Don't auto-select a new topper
    }
    
    const { market, lang } = get()
    try {
      const toppersRes = await fetch(buildApiUrl(`bed-variations/${bedVariation.slug}/toppers/?market=${market}&lang=${lang}`))
      if (toppersRes.ok) {
        const toppersData = await toppersRes.json()
        let toppers = toppersData?.options || []
        if (size) toppers = filterToppersForWidth(toppers, size)
        
        // Apply filtering logic for adjustable beds
        const isAdjustable = bedVariation.bed_type_value === 'adjustable'
        if (isAdjustable && size) {
          toppers = filterToppersForAdjustableBed(toppers, size)
        }
        
        const firstTopper = toppers[0]
        
        if (firstTopper) {
          const models = (firstTopper.three_d_models || []) as Array<{ 
            width_cm: number | null
            is_split: boolean
            reclined_side?: string | null
            three_d_model: { id: number, file_url: string, preview_url?: string | null } 
          }>
          
          // Find best matching model using reusable function
          const isAdjustable = bedVariation.bed_type_value === 'adjustable'
          const chosen = selectBestTopperModel(
            models,
            size,
            isAdjustable,
            firstTopper.three_d_model || null
          )
          
          if (chosen) {
            get().setBedComponent('topper', {
              id: firstTopper.id,
              name: firstTopper.title,
              slug: firstTopper.slug,
              offsets: firstTopper.offsets || { x: 0, y: 0, z: 0 },
              three_d_model: chosen,
              three_d_models: models, // Store full array for reclined model lookup
              texture_options: (firstTopper.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: any[] }>,
              currentTexture: ((firstTopper.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: any[] }>)[0]?.textures?.[0] || null,
            } as any)
          }
        }
      }
    } catch (topperErr) {
      console.warn('Failed to select topper for size:', topperErr)
    }
  },
  refreshCurrentModelForSelectedSize: () => {
    const bedVariation = get().getBedVariation()
    const size = get().bedConfig.bed.selectedSize
    if (!bedVariation || !size) return
    const preferSeamless = get().preferSeamless
    const candidates = bedVariation.models.filter((m: BedVariationModel) => m.width_cm === size)
    const modelAssignment = preferSeamless
      ? (candidates.find((m) => m.seamless === true) || candidates.find((m) => m.seamless !== false) || candidates[0])
      : (candidates.find((m) => m.seamless === false) || candidates[0])
    get().setCurrentModel(modelAssignment ? modelAssignment.three_d_model : null)
  },
  
  // Accessory actions
  addAccessory: (accessory: BedComponent) => set((state) => ({
    bedConfig: restrictBedOptions(state.bedConfig.bed.variation, {
      ...state.bedConfig,
      accessories: [...state.bedConfig.accessories, accessory]
    })
  })),
  removeAccessory: (accessoryId: number) => set((state) => ({
    bedConfig: {
      ...state.bedConfig,
      accessories: state.bedConfig.accessories.filter(acc => acc.id !== accessoryId)
    }
  })),
  toggleAccessory: (accessory: BedComponent) => set((state) => {
    if (!isBedOptionAllowed(state.bedConfig.bed.variation, 'accessories')) return {}
    const existingIndex = state.bedConfig.accessories.findIndex(acc => acc.id === accessory.id)
    if (existingIndex >= 0) {
      // Remove if already selected
      return {
        bedConfig: {
          ...state.bedConfig,
          accessories: state.bedConfig.accessories.filter(acc => acc.id !== accessory.id)
        }
      }
    } else {
      // Add if not selected
      let newAccessories = [...state.bedConfig.accessories]
      
      // Check if this is a tray - if so, remove any other trays first
      const isTray = accessory.slug?.includes('tray') || accessory.name?.toLowerCase().includes('tray')
      if (isTray) {
        // Remove any existing trays
        newAccessories = newAccessories.filter(acc => 
          !(acc.slug?.includes('tray') || acc.name?.toLowerCase().includes('tray'))
        )
      }
      
      // Add the new accessory
      newAccessories.push(accessory)
      
      return {
        bedConfig: {
          ...state.bedConfig,
          accessories: newAccessories
        }
      }
    }
  }),

  // Get computed values
  getCurrentBedSize: () => get().bedConfig.bed.selectedSize,
  getCurrentModel: () => get().bedConfig.bed.currentModel,
  getBedVariation: () => get().bedConfig.bed.variation,
}))

// Expose store on window for debugging (e.g., Playwright screenshots)
if (typeof window !== 'undefined') {
  (window as any).__appStore__ = useAppStore
}
